import { DateTime } from 'luxon';
import { emptyUser, toUser } from './user.js';
import { toNumber } from './utils/utils.js';
import { matchSpaceTimeAndSlotKey, parseSpaceTime } from './spacetime/spacetime.js';
import { startOfDayInZone, endOfDayInZone, toUTCISO } from './time.js';
const toPunchCardSummary = (punchCard, altPunch) => {
    return {
        id: punchCard.uuid,
        name: punchCard.definition.name,
        slug: punchCard.definition.slug,
        punches: altPunch ??
            Object.values(punchCard.slotLedger).reduce((acc, curr) => acc + curr, 0),
        limit: punchCard.definition.limitTickets,
        validFrom: punchCard.validFrom,
        validTo: punchCard.validTo,
        onePrSlot: punchCard.definition.onePrSlot,
        spaceTime: punchCard.definition.spacetime,
        revokedAt: punchCard.revokedAt,
        revocationReason: punchCard.revocationReason
    };
};
const newPunchCard = (def, uuid, owner, nextPeriod, now, timezone) => {
    const fromTo = nextPeriod
        ? punchCardNextPeriod(def.period, now, def.alignPeriod, timezone)
        : punchCardCurrentPeriod(def.period, now, def.alignPeriod, timezone);
    return {
        uuid,
        validFrom: toUTCISO(fromTo[0]),
        validTo: toUTCISO(fromTo[1]),
        boughtAt: toUTCISO(now),
        revokedAt: '',
        revocationReason: '',
        definition: def,
        owner: toUser(owner), // strip out attributes
        orderId: '',
        slotLedger: {}
    };
};
const toPunchCardPeriod = (data) => {
    const number = toNumber(data);
    if (![0, 1, 2, 3, 4, 6, 12].includes(number)) {
        return 0; // This is they way we do it
    }
    return number;
};
const toPunchCardDef = (data) => {
    const template = emptyPunchCardDef(''); // make sure we include new fields and provide defaults
    if (!data) {
        return template;
    }
    template.id = data.id ?? template.id;
    template.name = data.name ?? template.name;
    template.slug = data.slug ?? template.slug;
    template.isForSale = data.isForSale ?? template.isForSale;
    // Admin forms have stored the numbers as strings; "0" and 0 must read the
    // same, and a missing number keeps the default rather than becoming NaN
    // (ADR 0013, D32)
    template.price = toNumber(data.price, template.price);
    template.description = data.description ?? template.description;
    template.spacetime = data.spacetime ?? template.spacetime;
    template.onePrSlot = data.restrictToOnePrCalendarSlot ?? data.onePrSlot ?? template.onePrSlot;
    template.limitTickets = toNumber(data.limitTickets, template.limitTickets);
    template.period =
        data.period === undefined || data.period === null
            ? template.period
            : toPunchCardPeriod(data.period);
    // alignPeriod is the field written; soldFromBeginningOfPeriod is a legacy
    // name read only when alignPeriod is absent (D35)
    template.alignPeriod =
        data.alignPeriod ?? data.soldFromBeginningOfPeriod ?? template.alignPeriod;
    // Set only when present: an optional key holding undefined cannot be
    // written to Firestore.
    if (data.slotDiscount) {
        template.slotDiscount = {
            spacetime: data.slotDiscount.spacetime ?? '',
            discount: {
                type: data.slotDiscount.discount?.type ?? 'percentage',
                value: toNumber(data.slotDiscount.discount?.value)
            }
        };
    }
    if (data.eventDiscount) {
        template.eventDiscount = {
            type: data.eventDiscount.type ?? 'percentage',
            value: toNumber(data.eventDiscount.value)
        };
    }
    return template;
};
const emptyPunchCardDoc = (uuid) => {
    return {
        uuid,
        validFrom: '',
        validTo: '',
        boughtAt: '',
        revokedAt: '',
        revocationReason: '',
        definition: emptyPunchCardDef(''),
        owner: emptyUser(),
        orderId: '',
        slotLedger: {}
    };
};
const toPunchCardDoc = (data) => {
    const template = emptyPunchCardDoc('');
    if (!data) {
        return template;
    }
    template.uuid = data.uuid ?? template.uuid;
    template.validFrom = data.validFrom ?? template.validFrom;
    template.validTo = data.validTo ?? template.validTo;
    template.boughtAt = data.boughtAt ?? template.boughtAt;
    template.revokedAt = data.revokedAt ?? template.revokedAt;
    template.revocationReason = data.revocationReason ?? template.revocationReason;
    template.definition = toPunchCardDef(data.definition) ?? template.definition;
    template.owner = toUser(data.owner ?? template.owner);
    template.boughtByIfDifferent =
        data.boughtByIfDifferent ?? template.boughtByIfDifferent ?? null;
    template.orderId = data.orderId ?? template.orderId;
    template.slotLedger = data.slotLedger ?? template.slotLedger;
    return template;
};
const emptyPunchCardDef = (id) => {
    return {
        id,
        name: '',
        slug: '',
        isForSale: true,
        price: 0, // No not sell for 0 - this will be enforce by the system
        description: '',
        spacetime: '',
        onePrSlot: true,
        limitTickets: 0,
        period: 6,
        alignPeriod: false
        // No slotDiscount/eventDiscount keys: they are optional, and a key set to
        // undefined makes the client's setDoc of the whole site document throw —
        // which made every card product created in the admin unsaveable.
    };
};
/**
 * Whether a card can be sold for this many months. 0 (a card that never
 * expires) is not built: the editor does not offer it, and a stored 0 is
 * refused at sale rather than given one day or divided by (ADR 0013, D36).
 */
const isPunchCardPeriodValid = (period) => [1, 2, 3, 4, 6, 12].includes(period);
const assertPunchCardPeriod = (period) => {
    if (!isPunchCardPeriodValid(period)) {
        throw new Error(`A card must last 1, 2, 3, 4, 6 or 12 months, not ${period}`);
    }
};
const punchCardCurrentPeriod = (period, // length
now, alignPeriod, timezone) => {
    assertPunchCardPeriod(period);
    if (!alignPeriod) {
        // For non-aligned periods, use timezone-aware start/end of day
        const nowInZone = now.setZone(timezone);
        return [
            startOfDayInZone(nowInZone, timezone),
            endOfDayInZone(nowInZone.plus({ month: period }), timezone)
        ];
    }
    // Read the month in the site's zone: the server's now is UTC, and in the
    // first hours of a period UTC is still in the previous month (ADR 0014)
    const nowInZone = now.setZone(timezone);
    const currentMonth = nowInZone.month;
    const currentYear = nowInZone.year;
    // Determine the start month of the period
    const periodStartMonth = Math.floor((currentMonth - 1) / period) * period + 1;
    // Calculate the start and end of the period
    // For aligned periods, create dates in the site's timezone, then convert to UTC
    const startDate = DateTime.fromObject({
        year: currentYear,
        month: periodStartMonth,
        day: 1,
        hour: 0,
        minute: 0,
        second: 0,
        millisecond: 0
    }, { zone: timezone });
    const endDate = endOfDayInZone(startDate.plus({ months: period }).minus({ days: 1 }), timezone);
    return [startDate, endDate];
};
const punchCardNextPeriod = (period, now, alignPeriod, timezone) => {
    const [startDate] = punchCardCurrentPeriod(period, now, alignPeriod, timezone);
    return [
        startDate.plus({ months: period }),
        // The whole last day is included (ADR 0014)
        endOfDayInZone(startDate.plus({ months: period * 2 }).minus({ days: 1 }), timezone)
    ];
};
const punchCardSummaryFilter = ({ cards, cart, includeRevoked = false, includeValidFrom = false, dateTime = DateTime.now(), slotKey, siteData }) => {
    const punchesUsedIncludingCart = {};
    cards.forEach((cardSummary) => {
        punchesUsedIncludingCart[cardSummary.id] = cardSummary.punches;
        if (cart) {
            Object.values(cart.tickets).forEach((ticket) => {
                if (ticket.punchCardId === cardSummary.id ||
                    ticket.unitPrice.punchCardId === cardSummary.id) {
                    punchesUsedIncludingCart[cardSummary.id] =
                        (punchesUsedIncludingCart[cardSummary.id] ?? 0) + 1;
                }
            });
        }
    });
    return cards.filter((card) => {
        const validTo = DateTime.fromISO(card.validTo) > dateTime;
        const validFrom = DateTime.fromISO(card.validFrom) <= dateTime;
        const isRevoked = !!card.revokedAt;
        const hasTicketsLeft = card.limit > punchesUsedIncludingCart[card.id] || card.limit === 0;
        // Skip revoked cards unless explicitly included
        if (isRevoked && !includeRevoked)
            return false;
        // Skip cards not yet valid unless explicitly included
        if (!validFrom && !includeValidFrom)
            return false;
        // Check if spaceTime matches the slot (if both slotKey and site are provided)
        if (slotKey && siteData && card.spaceTime) {
            try {
                const spaceTime = parseSpaceTime({
                    str: card.spaceTime,
                    site: siteData
                });
                const spacetimeMatches = matchSpaceTimeAndSlotKey(slotKey, spaceTime);
                if (!spacetimeMatches)
                    return false;
            }
            catch {
                // If spacetime parsing fails, exclude the card (safer approach)
                return false;
            }
        }
        // Must be valid and have tickets left
        return validTo && hasTicketsLeft;
    });
};
export { emptyPunchCardDef, emptyPunchCardDoc, isPunchCardPeriodValid, toPunchCardSummary, punchCardCurrentPeriod, punchCardNextPeriod, toPunchCardDoc, toPunchCardDef, toPunchCardPeriod, newPunchCard, punchCardSummaryFilter };
//# sourceMappingURL=punchcard.js.map